import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, KeyRound, MailCheck, ShieldCheck } from "lucide-react";
import api, { formatApiError, setToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const shell = "min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 pt-20 relative overflow-hidden";
const card = "relative w-full max-w-md bg-white/5 border border-white/10 backdrop-blur-xl rounded-md p-6 sm:p-10 space-y-6";
const input = "w-full bg-transparent border-b border-white/25 py-3 text-white font-body text-sm focus:outline-none focus:border-[#C8956A]";
const label = "font-mono text-[10px] uppercase tracking-wider text-white/50 block mb-1";
const btn = "w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F5D5B0] text-[#0A0A0A] py-3.5 font-body font-bold text-sm disabled:opacity-60 hover:-translate-y-0.5 transition-transform";
const errBox = "font-body text-sm text-[#F5D5B0] bg-[#6B1414]/60 border border-[#F5D5B0]/30 rounded-md p-3";

const Head = ({ eyebrow, title }) => (
  <div className="flex items-center gap-3">
    <div className="bg-white rounded-md p-1.5"><img src="/logo-mark.png" alt="Calium" className="h-10 w-10 object-contain" /></div>
    <div>
      <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#C8956A]">{eyebrow}</p>
      <h1 className="font-heading text-2xl font-black text-white tracking-tight">{title}</h1>
    </div>
  </div>
);

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ loading: false, error: "", done: false });
  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "", done: false });
    try {
      await api.post("/auth/forgot-password", { email });
      setState({ loading: false, error: "", done: true });
    } catch (err) {
      setState({ loading: false, error: formatApiError(err), done: false });
    }
  };
  return (
    <div className={shell} data-testid="forgot-password-page">
      <div className="absolute inset-0 grain opacity-[0.06] pointer-events-none" />
      <form onSubmit={submit} className={card}>
        <Head eyebrow="Control Room" title="Forgot password" />
        {state.done ? (
          <div data-testid="forgot-success" className="space-y-3 text-white/80 font-body text-sm">
            <MailCheck size={28} className="text-[#C8956A]" />
            <p>If an account exists for <strong className="text-white">{email}</strong>, a reset link is on its way. It expires in 1 hour.</p>
            <Link to="/admin/login" className="inline-block font-mono text-xs uppercase tracking-wider underline underline-offset-4 text-[#C8956A]">Back to sign in</Link>
          </div>
        ) : (
          <>
            <p className="font-body text-sm text-white/60">Enter your Control Room email and we&apos;ll send a secure link to choose a new password.</p>
            <div>
              <label className={label}>Email</label>
              <input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} placeholder="you@calium.in" />
            </div>
            {state.error && <p data-testid="forgot-error" className={errBox}>{state.error}</p>}
            <button data-testid="forgot-submit-btn" disabled={state.loading} className={btn}>{state.loading ? "Sending..." : "Send reset link"} <ArrowRight size={14} /></button>
            <Link to="/admin/login" data-testid="forgot-back-link" className="block text-center font-mono text-[10px] uppercase tracking-wider text-white/50 hover:text-white">Back to sign in</Link>
          </>
        )}
      </form>
    </div>
  );
}

export function SetPasswordPage({ mode }) {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState({ a: "", b: "" });
  const [loading, setLoading] = useState(false);
  const isInvite = mode === "verify";

  useEffect(() => {
    if (!token) return setError("This link is missing its token.");
    api.get("/auth/token-info", { params: { token } }).then((r) => setInfo(r.data)).catch((err) => setError(formatApiError(err)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return setError("Passwords do not match.");
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw.a });
      setToken(data.access_token);
      await refresh();
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={shell} data-testid={isInvite ? "verify-page" : "reset-page"}>
      <div className="absolute inset-0 grain opacity-[0.06] pointer-events-none" />
      <form onSubmit={submit} className={card}>
        <Head eyebrow={isInvite ? "Email verification" : "Password reset"} title={isInvite ? "Activate your account" : "Choose a new password"} />
        {info && (
          <p data-testid="set-password-email" className="font-body text-sm text-white/70 flex items-center gap-2">
            <ShieldCheck size={16} className="text-[#C8956A]" /> {isInvite ? "Verifying" : "Resetting"} <strong className="text-white">{info.email}</strong>
          </p>
        )}
        {info && (
          <>
            <div>
              <label className={label}>New password (min 8 characters)</label>
              <input data-testid="set-password-input" type="password" required minLength={8} value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className={input} placeholder="••••••••" />
            </div>
            <div>
              <label className={label}>Confirm password</label>
              <input data-testid="set-password-confirm-input" type="password" required value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} className={input} placeholder="••••••••" />
            </div>
          </>
        )}
        {error && <p data-testid="set-password-error" className={errBox}>{error}</p>}
        {info ? (
          <button data-testid="set-password-submit-btn" disabled={loading} className={btn}><KeyRound size={14} /> {loading ? "Saving..." : isInvite ? "Verify & enter Control Room" : "Save new password"}</button>
        ) : (
          <Link to="/admin/forgot" className="block text-center font-mono text-[10px] uppercase tracking-wider text-[#C8956A] underline underline-offset-4">Request a new link</Link>
        )}
      </form>
    </div>
  );
}
